import { _js } from '@ifixit/utils/localize';
import { DangerConfirmButton, PrimaryButton } from 'Shared/editor/buttons';

/**
 * Announces the in-flight write: the disabled attribute blurs the pressed
 * button mid-request, so its label change alone is silent for screen
 * readers. Mount it at the editor's root, never in the action bar — it has
 * to outlive the write and stay outside any region the page makes `inert`,
 * which hides a nested live region from assistive tech. A region that
 * mounts, remounts, or arrives with the write announces nothing.
 */
export function WriteStatusAnnouncer({
   isSaving,
   isDeleting = false,
   isApproving = false,
}: {
   isSaving: boolean;
   isDeleting?: boolean;
   isApproving?: boolean;
}) {
   return (
      <span role="status" className="sr-only">
         {isSaving
            ? _js('Saving...')
            : isDeleting
              ? _js('Deleting...')
              : isApproving
                ? _js('Approving...')
                : ''}
      </span>
   );
}

/**
 * Sits as the last child of a SectionCard: the negative margins cancel the
 * card's padding so the bar spans its full width and settles into its
 * bottom edge, sticking to the viewport while the card scrolls. Exported
 * for the guide editors, which build their own bar bodies but need the same
 * bleed, and it is coupled to SectionCard's padding.
 */
export const BAR_CLASSES =
   'sticky bottom-0 z-10 -mx-6 -mb-8 mt-6 rounded-b-2xl border-t border-gray-200 bg-white px-6 py-3 max-md:-mx-4 max-md:-mb-4 max-md:rounded-b-none max-md:px-4';

/**
 * The wiki editors' action bar: optional delete on the left, optional
 * preview plus save on the right.
 */
export function EditorActionBar({
   canDelete,
   deleteLabel,
   deleteTitle,
   deleteDescription,
   isSaving,
   isDeleting,
   isPreviewing = false,
   saveDisabled = false,
   onSave,
   onDelete,
   onPreview,
}: {
   canDelete: boolean;
   deleteLabel: string;
   deleteTitle: string;
   deleteDescription: string;
   isSaving: boolean;
   isDeleting: boolean;
   isPreviewing?: boolean;
   /** Extra save blockers beyond the write lock (e.g. image processing). */
   saveDisabled?: boolean;
   onSave: () => void;
   onDelete: () => void;
   onPreview?: () => void;
}) {
   return (
      <div className={BAR_CLASSES}>
         <div className="flex flex-wrap items-center justify-between gap-2">
            {canDelete ? (
               <DangerConfirmButton
                  label={deleteLabel}
                  pendingLabel={_js('Deleting...')}
                  title={deleteTitle}
                  description={deleteDescription}
                  isPending={isDeleting}
                  disabled={isSaving}
                  disabledReason={isSaving ? _js('Wait for the save to finish.') : undefined}
                  onConfirm={onDelete}
               />
            ) : (
               <span />
            )}
            <div className="flex items-center gap-2">
               {onPreview && (
                  <button
                     className="button-md button-outline"
                     // Also locked during save/delete: those paths redirect
                     // on success, and a preview racing location.assign
                     // renders against a page that's already leaving.
                     disabled={isPreviewing || isSaving || isDeleting}
                     onClick={onPreview}
                  >
                     {isPreviewing ? _js('Loading changes...') : _js('Show changes')}
                  </button>
               )}
               <PrimaryButton
                  isPending={isSaving}
                  disabled={isDeleting || saveDisabled}
                  onClick={onSave}
               >
                  {isSaving ? _js('Saving...') : _js('Save')}
               </PrimaryButton>
            </div>
         </div>
      </div>
   );
}
